import {GraphQL} from "../graphql"
import * as React from "react"
import {Divider, Form, Header, Loader, Message, Segment} from "semantic-ui-react"
import {CommonPlacesLocale} from "./CommonPlacesLocale"
import {
  AddressInput,
  BookerProfileData,
  UpdateBooker as UPDATE_BOOKER,
  UpdateBookerMutationVariables,
} from "../graphql/queries"
import {ID} from "../model/id"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {WithModalStore} from "../App"
import {isValidEmailAddress} from "../email"
import {history} from "../App"

export interface BookerData {
  name: string
  email: string
  isEmailVerified: boolean
  invoiceTerms: string
  isSocialAccount: boolean
  localeId: ID
  accountsEmail: string
  address: AddressInput
  loyaltyCode: string
  loyaltyDiscount: string
  giftLoyaltyCredits: boolean
}

function emptyAddress(): AddressInput {
  return {
    attention: "",
    address: "",
    postCode: "",
    state: "",
    country: "",
    suburb: "",
  }
}

interface Props extends OwnProps {
  booker: BookerData
}

function AddressForm({
  address: whole,
  address: {attention, address, postCode, state, country, suburb},
  onAddress,
}: {
  address: AddressInput
  onAddress: (address: AddressInput) => void
}) {
  return (
    <div>
      <Form.Input
        value={attention}
        placeholder="Attention"
        onChange={(e, x) => {
          onAddress({...whole, attention: x.value})
        }}
      />
      <Form.TextArea
        value={address}
        placeholder="Address"
        onChange={(e, x) => {
          onAddress({...whole, address: x.value + ""})
        }}
      />
      <Form.Input
        value={suburb}
        placeholder="Suburb"
        onChange={(e, x) => {
          onAddress({...whole, suburb: x.value})
        }}
      />
      <Form.Group>
        <Form.Input
          value={state}
          placeholder="State"
          onChange={(e, x) => {
            onAddress({...whole, state: x.value})
          }}
        />
        <Form.Input
          value={postCode}
          placeholder="Post Code"
          onChange={(e, x) => {
            onAddress({...whole, postCode: x.value})
          }}
        />
      </Form.Group>
      <Form.Input
        value={country}
        placeholder="Country"
        onChange={(e, x) => {
          onAddress({...whole, country: x.value})
        }}
      />
    </div>
  )
}

interface State {
  booker: BookerData
}

class ProfileForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      booker: props.booker,
    }
  }

  render() {
    const {booker} = this
    const {isAdmin} = this.props

    const {
      name,
      email,
      isEmailVerified,
      localeId,
      isSocialAccount,
      accountsEmail,
      invoiceTerms,
      loyaltyCode,
      loyaltyDiscount,
      giftLoyaltyCredits,
    } = booker

    const emailMessage = isSocialAccount
      ? "You are logged in using a social media account and cannot change your email address here."
      : "Please take care when changing your email address. We will send an account " +
        "verification email to your new address."

    const passwordMessage = isSocialAccount
      ? "You are logged in using a social media account and so do not have a password."
      : "To change your password simply Logout and then click the 'Forgot Password' link."

    const emailError = !isValidEmailAddress(email)
    const accountsEmailError = Boolean(accountsEmail && !isValidEmailAddress(accountsEmail))
    const invoiceTermsError = Number.isNaN(Number.parseInt(invoiceTerms, 10))
    const loyaltyDiscountError = Number.isNaN(Number.parseFloat(loyaltyDiscount))
    // noinspection UnnecessaryLocalVariableJS
    const saveDisabled =
      emailError || accountsEmailError || invoiceTermsError || loyaltyDiscountError

    return (
      <Segment.Group>
        <Segment inverted color="olive">
          <Form>
            <Header as="h3">Booker Profile</Header>
            <Form.Field>
              <label>Name</label>
              <Form.Input
                placeholder={"Label"}
                value={name}
                onChange={(event, {value}) => {
                  this.onName(value)
                }}
              />
            </Form.Field>
            <Form.Field error={emailError}>
              <label>Email</label>
              {/* For some reason the message box is hidden by default but I couldn't figure out what
       style rule it was coming from or what the correct way is to show it, so I'm just going
       to set it to display: block manually.  */}
              <Message style={{display: "block"}} warning size="small">
                {emailMessage}
              </Message>
              <Form.Input
                fluid
                type="email"
                placeholder={"Email"}
                value={email}
                disabled={isSocialAccount}
                onChange={(event, {value}) => {
                  this.onEmail(value)
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Email address validated?</label>
              <span>{isEmailVerified ? "Yes" : "No"}</span>
            </Form.Field>
            <Form.Field>
              <label>Password</label>
              <Message style={{display: "block"}} warning size="small">
                {passwordMessage}
              </Message>
            </Form.Field>
            <CommonPlacesLocale
              localeId={localeId}
              onLocale={(locale) => {
                this.onLocaleId(locale.id)
              }}
            />
            <Form.Field>
              <label>Your Loyalty code</label>
              <span>{loyaltyCode}</span>
            </Form.Field>
            <Form.Field>
              <label>Gift your loyalty credits to charity by default?</label>
              <Form.Checkbox
                checked={giftLoyaltyCredits}
                onChange={(e, {checked}) => {
                  this.onGiftLoyaltyCredits(checked || false)
                }}
              />
            </Form.Field>
            {isAdmin && (
              <Form.Field error={loyaltyDiscountError}>
                <label>Agent Discount %</label>
                <Form.Input
                  width="5"
                  fluid
                  type="number"
                  placeholder="Eg. 0, 5 or 8.5"
                  value={loyaltyDiscount}
                  onChange={(event, {value}) => {
                    this.onLoyaltyDiscount(value)
                  }}
                />
              </Form.Field>
            )}
            <Form.Field error={accountsEmailError}>
              <label>Accounts Email</label>
              <Form.Input
                fluid
                type="email"
                placeholder={"Accounts Email"}
                value={accountsEmail}
                onChange={(event, {value}) => {
                  this.onAccountsEmail(value)
                }}
              />
            </Form.Field>
            <Form.Field error={invoiceTermsError}>
              <label>Invoice Terms</label>
              <Form.Input
                width="5"
                disabled={!isAdmin}
                fluid
                type="number"
                placeholder="Eg. 0, 7 or 14."
                value={invoiceTerms}
                onChange={(event, {value}) => {
                  this.onInvoiceTerms(value)
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Billing Address</label>
              <AddressForm address={booker.address} onAddress={this.onAddress} />
            </Form.Field>
            <Divider />
            <Form.Group>
              <Form.Button
                positive
                disabled={saveDisabled}
                children={"Save"}
                onClick={(e) => {
                  e.preventDefault()
                  this.modalStore.handlePromiseThrow(this.onSave())
                }}
              />
              <Form.Button
                children={"Reset"}
                onClick={(e) => {
                  e.preventDefault()
                  this.onReset()
                }}
              />
            </Form.Group>
          </Form>
        </Segment>
      </Segment.Group>
    )
  }

  onSave = () =>
    this.props.modalStore.applyModal({
      pending: ["Saving profile", "Saving your profile..."],
      ok: () => ["Profile saved", "Your profile has been saved."],
      error: (m) => ["Saving profile failed", m],
      action: () => this.onSaveRaw(),
    })

  onSaveRaw = async () => {
    const {
      name,
      email,
      localeId,
      accountsEmail,
      invoiceTerms,
      loyaltyDiscount,
      address: addressParts,
      giftLoyaltyCredits,
    } = this.booker
    const {address, attention, suburb, country, postCode, state} = addressParts
    const {forBookerId} = this.props
    // Return the promise so the caller can wait for the booker to be saved
    const variables: UpdateBookerMutationVariables = {
      forBookerId,
      booker: {
        name,
        email,
        localeId,
        accountsEmail,
        invoiceTerms: Number.parseInt(invoiceTerms, 10),
        loyaltyDiscount: Number.parseFloat(loyaltyDiscount),
        address: {address, attention, state, postCode, country, suburb},
        giftLoyaltyCredits,
      },
    }
    const result = await GraphQL.mutate(UPDATE_BOOKER, variables)
    return errorUnionToEither(result.updateBooker).map(() => {
      history.push("/bookings")
      return void 0
    })
  }

  get modalStore() {
    return this.props.modalStore
  }

  onName = (x: string): void => {
    this.setBooker({name: x})
  }
  onEmail = (x: string): void => {
    this.setBooker({email: x})
  }
  onLocaleId = (x: ID): void => {
    this.setBooker({localeId: x})
  }
  onGiftLoyaltyCredits = (x: boolean): void => {
    this.setBooker({giftLoyaltyCredits: x})
  }
  onAccountsEmail = (x: string): void => {
    this.setBooker({accountsEmail: x})
  }
  onInvoiceTerms = (x: string): void => {
    this.setBooker({invoiceTerms: x})
  }
  onLoyaltyDiscount = (x: string): void => {
    this.setBooker({loyaltyDiscount: x})
  }
  onAddress = (x: AddressInput): void => {
    this.setBooker({address: x})
  }

  setBooker(x: Partial<BookerData>) {
    this.setState(({booker}) => ({booker: {...booker, ...x}}))
  }

  get booker() {
    return this.state.booker
  }

  onReset = () => {
    this.setBooker(this.props.booker)
  }
}

interface OwnProps extends WithModalStore {
  forBookerId: ID
  isAdmin: boolean
}

function WithGetBooker(props: OwnProps) {
  const {forBookerId} = props
  return (
    <GraphQL.Query
      query={BookerProfileData}
      variables={{forBookerId}}
      // We use "network-only" so that the form state will be initialised from a fresh fetch of
      // the booker data. If we do "cache-and-network" then the form state will be initialised from
      // the cached booker and when the new booker arrives from the network it will have no effect.
      fetchPolicy="network-only"
      render={({data, loading}) => {
        if (data && !loading) {
          return handleError(errorUnionToEither(data.booker), (data) => {
            if (data.booker) {
              const {booker} = data
              return (
                <ProfileForm
                  {...props}
                  modalStore={props.modalStore}
                  booker={{
                    name: booker.name,
                    email: booker.email,
                    isSocialAccount: booker.isSocialAccount,
                    isEmailVerified: booker.isEmailVerified,
                    invoiceTerms: booker.invoiceTerms.toString(),
                    localeId: booker.defaultLocale.id,
                    accountsEmail: booker.accountsEmail,
                    address: booker.address != null ? booker.address : emptyAddress(),
                    loyaltyCode: booker.loyaltyCode,
                    loyaltyDiscount: booker.loyaltyDiscount.toString(),
                    giftLoyaltyCredits: booker.giftLoyaltyCredits,
                  }}
                />
              )
            }
            // The only situation I could reproduce in which we have
            // no booker is if the user isn't logged in.
            return <div>No booker. Please log in.</div>
          })
        }
        return <Loader active content="Loading Booker Profile..." />
      }}
    />
  )
}

export {WithGetBooker as Profile}



// WEBPACK FOOTER //
// ./src/components/Profile.tsx